import { Link } from 'react-router-dom';
import { useRitualCollectivePerks } from '../hooks/useRitualCollective';
import styles from '../styles/pages/RitualCollective.module.scss';
import Spinner from '../components/core/Spinner';
import ExpandArrow from '../assets/icons/arrow-expand.svg';
import CopyIcon from '../assets/icons/copy-icon.svg';
import { useState, useEffect } from 'react';
import ErrorScreen from '../components/core/ErrorMessages';
import { useToast } from '../components/core/ToastManager';
import Modal from '../components/core/Modal';
import RitualPerkTermsModal from '../components/RitualPerkTermsModal';
import DOMPurify from "dompurify";

export const RitualCollective = () => {
  const [openTerms, setOpenTerms] = useState('');
  const [expandedPerks, setExpandedPerks] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerRow, setItemsPerRow] = useState(1);
  const [loadedPerks, setLoadedPerks] = useState<any[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  let totalPages = 0;
  const itemsPerPage = 9;

  const { data: perks, isLoading: isPerksLoading, error: perksError } = useRitualCollectivePerks({ page: currentPage, perPage: itemsPerPage });

  const { addToast } = useToast();

  // Detect screen size and update itemsPerRow
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 992) {
        setItemsPerRow(3);
      } else if (width >= 768) {
        setItemsPerRow(2);
      } else {
        setItemsPerRow(1);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (perks?.data?.ritualCollectivePerks) {
      setLoadedPerks((prev) => [...prev, ...perks.data.ritualCollectivePerks]);
      setIsFetching(false);
    }
    if (!totalPages) {
      totalPages = perks?.meta?.pagination.totalPages || 0
    }
  }, [perks]);

  useEffect(() => {
    const handleScroll = () => {
      if (isFetching || isPerksLoading) return;

      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const fullHeight = document.documentElement.scrollHeight;

      if (scrollY + windowHeight >= fullHeight - 100 && totalPages > currentPage) {
        setIsFetching(true);
        setCurrentPage((prev) => prev + 1);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isFetching, isPerksLoading]);

  const handleExpandToggle = (id: string) => {
    if (!perks) return;

    // Find the index of the clicked perk in the current page
    const clickedIndex = loadedPerks.findIndex(perk => perk.id === id);

    if (clickedIndex === -1) return;

    // Calculate which row this perk is in
    const rowIndex = Math.floor(clickedIndex / itemsPerRow);

    // Get all perk IDs in the same row
    const startIdx = rowIndex * itemsPerRow;
    const endIdx = Math.min(startIdx + itemsPerRow, loadedPerks.length);
    const rowPerkIds = loadedPerks.slice(startIdx, endIdx).map(perk => perk.id);

    // Check if all perks in this row are already expanded
    const allExpanded = rowPerkIds.every(perkId => expandedPerks.includes(perkId));

    if (allExpanded) {
      // Collapse all perks in this row
      setExpandedPerks(prev => prev.filter(perkId => !rowPerkIds.includes(perkId)));
    } else {
      // Expand all perks in this row
      setExpandedPerks(prev => {
        const filtered = prev.filter(perkId => !rowPerkIds.includes(perkId));
        return [...filtered, ...rowPerkIds];
      });
    }
  };

  const handleCopyToClipboard = (code: string) => {
    navigator.clipboard.writeText(code);
    addToast(`Code "${code}" copied to clipboard`, 'success');
  }

  if (isPerksLoading && currentPage === 1) return <Spinner />;
  if (perksError) return <ErrorScreen errors={[perksError]} />;

  return (
    <div className={styles.ritualCollective}>
      <h1 className={styles.pageTitle}>The Ritual Collective</h1>
      <p className={styles.pageDescription}>
        Elevate your self-care routine with premium perks from our favorite brands.<br /> Exclusively for <em>Ritual Members</em>.
      </p>
      <div className={styles.perks}>
        {loadedPerks?.map((perk) => {
          const sanitizedHTML = DOMPurify.sanitize(perk.description)
          return (
            <div key={perk.id} className={`${styles.perk} ${expandedPerks.includes(perk.id) ? styles.expandedPerk : ''}`}>
              <div>
                <img src={perk.imageUrl} alt={perk.description} />
                <h3>{perk.title}</h3>
                <p className={styles.subTitle}>{perk.subTitle}</p>
                <div className={`${styles.description}  ${expandedPerks.includes(perk.id) ? styles.active : ''}`}>
                  <p dangerouslySetInnerHTML={{ __html: sanitizedHTML }}></p>
                  {perk.termsAndConditions && <button className={`button__link ${styles.termsLink}`} onClick={() => setOpenTerms(perk.id)}>Terms & Conditions Apply</button>}
                </div>
              </div>
              <div>
                <button
                  className={styles.expandButton}
                  onClick={() => handleExpandToggle(perk.id)}
                >
                  <img
                    src={ExpandArrow}
                    alt={expandedPerks.includes(perk.id) ? 'View less' : 'View more'}
                    className={expandedPerks.includes(perk.id) ? styles.active : ''}
                  />
                  {expandedPerks.includes(perk.id) ? 'View less' : 'View more'}
                </button>
                {(perk.code && perk.code.trim() || (perk.ctaText && perk.link)) && (
                  <div className={`${styles.actions} ${perk.code ? styles.hasCode : ''}`}>
                    {perk.code.trim() && (
                      <div className={styles.code}>
                        <p>{perk.code}</p>
                        <button onClick={() => handleCopyToClipboard(perk.code || '')}>
                          <img src={CopyIcon} alt="copy code" />
                        </button>
                      </div>
                    )}
                    {perk.ctaText && perk.link && (
                      <Link className={`button inverted ${styles.cta}`} to={perk.link}>
                        {perk.ctaText}
                      </Link>
                    )}
                  </div>
                )}
              </div>
              <Modal isOpen={openTerms === perk.id} onClose={() => setOpenTerms('')}>
                <RitualPerkTermsModal title={perk.title} body={perk.termsAndConditions} />
              </Modal>
            </div>
          )
        })}
      </div>
    </div>
  );
};
